import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { DriveDeskAIIcon } from "@/components/icons/DriveDeskAIIcon";
import { useTour } from "@/context/TourContext";

const FEATURE_BULLETS = [
  "Daily Brief: what matters today",
  "Business Pulse: trends at a glance",
  "🤖 Your AI Office Manager: action plans you can execute",
  "Scheduling + Parent Portal: fewer back-and-forths",
];

/**
 * v1.3 Presentation Mode polish — the first-visit welcome prompt for the
 * Guided Tour. Shown at most once per browser session (state owned by
 * TourContext); dismissing it either way (Start Tour or Explore on My
 * Own) never interrupts normal navigation again this session.
 */
export const TourWelcomeDialog = () => {
  const { showWelcome, startTour, dismissWelcome } = useTour();

  return (
    <Dialog open={showWelcome} onOpenChange={(open) => !open && dismissWelcome()}>
      <DialogContent
        className="max-w-[720px] p-0 overflow-hidden gap-0"
        data-testid="tour-welcome-dialog"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          document.getElementById("tour-welcome-start-button")?.focus();
        }}
      >
        <div className="grid md:grid-cols-2">
          <div className="p-6 md:p-8">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[hsl(var(--presentation-soft))] text-[hsl(var(--presentation))]"
              data-testid="tour-welcome-showcase-chip"
            >
              <Sparkles className="h-3 w-3" /> Showcase
            </span>
            <DialogTitle className="mt-3 font-heading text-xl font-semibold tracking-tight" data-testid="tour-welcome-title">
              👋 Welcome to DriveDesk Hub
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm text-muted-foreground leading-relaxed" data-testid="tour-welcome-subtitle">
              Would you like a quick two-minute tour highlighting what makes DriveDesk Hub different?
            </DialogDescription>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Button
                id="tour-welcome-start-button"
                onClick={startTour}
                data-testid="tour-welcome-start-button"
              >
                Start Tour
              </Button>
              <Button variant="outline" onClick={dismissWelcome} data-testid="tour-welcome-skip-button">
                Explore on My Own
              </Button>
            </div>
          </div>
          <div className="hidden md:flex md:flex-col md:border-l border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-6 md:p-8 justify-center">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <DriveDeskAIIcon className="h-5 w-5" size="sm" variant="circle" />
              What you&apos;ll see
            </div>
            <ul className="mt-3 space-y-2.5">
              {FEATURE_BULLETS.map((bullet) => (
                <li key={bullet} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--role-accent))] shrink-0" />
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
